import { noop, useIntl, useQuantity } from '@capsicum/common';
import { type InputHTMLAttributes } from 'react';
import { type FieldError } from 'react-hook-form';
import styled, { css, useTheme } from 'styled-components';

import { InteractionLayer } from '../../../atoms/interaction-layer/InteractionLayer';
import { InfoIcon, MinusBoldIcon, PlusBoldIcon } from '../../../icons';

export type BaseQuantityInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> & {
	disabledAddition?: boolean;
	disabledSubstraction?: boolean;
	error?: FieldError;
	fullWidth?: boolean;
	label?: string;
	max?: number;
	min?: number;
	onChange?: (value: number) => void;
	onError?: (value: boolean) => void;
	size?: 'medium' | 'small';
	step?: number;
	testID: string;
	value: number;
};

/**
 * @param {BaseQuantityInputProps} props - The component's props
 * @param {string} [props.testID] - Add custom testID parent name
 * @param {boolean} [props.disabledAddition] - If the plus button should be disabled
 * @param {boolean} [props.disabledSubstraction] - If the minus button should be disabled
 * @param {number} [props.value] - Manages the input value from the parent
 * @param {string} [props.label] - Add a custom input label title
 * @param {string} [props.error] - Add a custom error message title
 * @param {string} [props.name] - to identify input
 * @param {Function} [props.onChange] - on change event callback
 * @param {Function} [props.onError] - on error callback
 * @param {number} [props.min] - Minimun quantity allowed
 * @param {number} [props.max] - Maximun quantity allowed
 * @param {number} [props.step] - Range of quantity added or subtracted on Press
 * @param {'small' | 'medium'} [props.size] - Select between available input sizes
 * @param {boolean} [props.fullWidth] - Allows a full width input
 */
export function BaseQuantityInput({
	defaultValue = 0,
	disabledAddition,
	disabledSubstraction,
	error,
	fullWidth = false,
	label = '',
	max,
	min,
	name = 'input-quantity',
	onChange,
	onError,
	size,
	step = 1,
	testID = 'input-quantity',
	value = 0,
	...props
}: BaseQuantityInputProps) {
	const { colors } = useTheme();
	const { $t } = useIntl();
	const theme = useTheme();

	const {
		addQuantity,
		handleChange,
		isAdditionDisabled,
		isSubtractionDisabled,
		quantityInputErrorMessage,
		subtractQuantity
	} = useQuantity({
		disabledAddition,
		disabledSubstraction,
		max,
		min,
		onError,
		step,
		value: parseInt('' + value, 0)
	});

	return (
		<>
			{label && <S.Label htmlFor={name}>{label}</S.Label>}
			<S.QuantityContainer data-testid={testID}>
				<S.ButtonInteractionLayer
					$type="subtract"
					disabled={isSubtractionDisabled}
					onClick={() => {
						const quantity = subtractQuantity();

						onChange?.(quantity);
					}}
				>
					<S.ButtonContainer
						$isError={!!error}
						$size={size}
						$type="subtract"
						data-testid={`${testID}-subtract-button`}
					>
						<MinusBoldIcon
							color={colors.icons.main.enabled}
							height={theme.sizing.small}
							width={theme.sizing.small}
						/>
					</S.ButtonContainer>
				</S.ButtonInteractionLayer>
				<S.InputInteractionLayer $fullWidth={fullWidth} onClick={noop}>
					<S.Input
						$fullWidth={fullWidth}
						$isError={!!error}
						$size={size}
						data-testid={`${testID}-field`}
						defaultValue={defaultValue}
						id="inputName"
						max={max}
						min={min}
						name={name}
						readOnly
						type="number"
						value={value}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							handleChange(e.target.value);
							onChange?.(Number(e.target.value));
						}}
						{...props}
					/>
				</S.InputInteractionLayer>
				<S.ButtonInteractionLayer
					$type="add"
					disabled={isAdditionDisabled}
					onClick={() => {
						const quantity = addQuantity();

						onChange?.(quantity);
					}}
				>
					<S.ButtonContainer
						$isError={Boolean(quantityInputErrorMessage)}
						$size={size}
						$type="add"
						data-testid={`${testID}-add-button`}
					>
						<PlusBoldIcon
							color={colors.icons.main.enabled}
							height={theme.sizing.small}
							width={theme.sizing.small}
						/>
					</S.ButtonContainer>
				</S.ButtonInteractionLayer>
			</S.QuantityContainer>
			{error && (
				<S.ErrorContainer>
					<InfoIcon color={colors.icons.error.enabled} height="13" width="13"></InfoIcon>
					{error?.message}
				</S.ErrorContainer>
			)}
		</>
	);
}

namespace S {
	export const QuantityContainer = styled.div`
		display: flex;
		position: relative;
		flex-direction: row;
	`;

	export const InputInteractionLayer = styled(InteractionLayer)<{
		$fullWidth: BaseQuantityInputProps['fullWidth'];
	}>`
		flex-grow: ${({ $fullWidth }) => ($fullWidth ? 1 : 0)};
		display: flex;
		flex-direction: row;
	`;

	export const Input = styled.input<{
		$fullWidth: boolean;
		$isError: boolean;
		$size: BaseQuantityInputProps['size'];
	}>`
		flex-grow: ${({ $fullWidth }) => ($fullWidth ? 1 : 0)};
		width: ${({ $size, theme }) => ($size === 'small' ? theme.sizing.large : theme.sizing.xlarge)};
		height: ${({ $size, theme }) => ($size === 'small' ? theme.sizing.large : theme.sizing.xlarge)};
		position: relative;
		outline: none;
		apperance: none;
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		border-radius: 0;
		border: solid ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.selected};
		${({ $isError }) =>
			$isError &&
			css`
				border-top-color: ${({ theme }) => theme.colors.borders.error.enabled};
				border-bottom-color: ${({ theme }) => theme.colors.borders.error.enabled};
			`}
		text-align: center;
		padding: 0;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text1};
		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}
		&[type='number'] {
			-moz-appearance: textfield;
		}
	`;

	export const ButtonInteractionLayer = styled(InteractionLayer)<{ $type: 'add' | 'subtract' }>`
		cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
		pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
		${({ theme }) => theme.border.radius.medium};
		${({ $type }) =>
			$type === 'add'
				? css`
						border-top-left-radius: 0;
						border-bottom-left-radius: 0;
					`
				: css`
						border-top-right-radius: 0;
						border-bottom-right-radius: 0;
					`};
	`;

	export const ButtonContainer = styled.div<{
		$isError: boolean;
		$size: BaseQuantityInputProps['size'];
		$type: 'add' | 'subtract';
	}>`
		display: flex;
		justify-content: center;
		align-items: center;
		width: ${({ $size, theme }) => ($size === 'small' ? theme.sizing.large : theme.sizing.xlarge)};
		height: ${({ $size, theme }) => ($size === 'small' ? theme.sizing.large : theme.sizing.xlarge)};
		border: solid ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.selected};
		${({ $isError, $type }) =>
			$isError &&
			($type === 'add'
				? css`
						border-top-color: ${({ theme }) => theme.colors.borders.error.enabled};
						border-bottom-color: ${({ theme }) => theme.colors.borders.error.enabled};
						border-right-color: ${({ theme }) => theme.colors.borders.error.enabled};
					`
				: css`
						border-top-color: ${({ theme }) => theme.colors.borders.error.enabled};
						border-bottom-color: ${({ theme }) => theme.colors.borders.error.enabled};
						border-left-color: ${({ theme }) => theme.colors.borders.error.enabled};
					`)}
		${({ theme }) => theme.border.radius.medium};
		${({ $type }) =>
			$type === 'add'
				? css`
						border-top-left-radius: 0;
						border-bottom-left-radius: 0;
						border-left-width: 0;
					`
				: css`
						border-top-right-radius: 0;
						border-bottom-right-radius: 0;
						border-right-width: 0;
					`};
	`;
	export const ErrorContainer = styled.span`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.destructive.enabled};
		text-align: left;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const Label = styled.label`
		${({ theme }) => theme.fonts.text.text5};
		text-align: left;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		display: block;
	`;
}

export default BaseQuantityInput;
